import { HudDrawer, useHudDrawerSideLabel } from '../HudDrawer';
import type { HudEdge } from '@sorrem/shared';
import type { UICoalescence, UIInventoryItem, UIItemComparisons } from '@sorrem-client/types';
import { HudPanel } from '../HudPanel';
import { CarriedSlotContent } from '../CarriedSlotContent';
import { HotkeySlot } from '../HotkeySlot';
import { useItemTransferContext } from '../ItemTransfer';
import { CarriedPanelGuide } from './components/CarriedPanelGuide';
import { findCarriedSlotHotkeyIndex } from '@sorrem-client/ui/shared/action-bar';
import { useActionBarKeyLabels, useActionBarTargets } from '@sorrem-client/ui/hooks';
import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import styles from './CarriedPanel.module.scss';

interface CarriedPanelProps {
    /** Slides the panel in from the left edge; closed, only its side label shows. */
    open: boolean;
    /** Opens or closes the panel (its side label is clickable). */
    onOpenChange: (open: boolean) => void;
    /** What occupies each carried slot (holes are empty slots). */
    carriedItems: ReadonlyArray<UIInventoryItem | undefined>;
    /** How many carried slots there are. */
    totalSlots: number;
    /** Crystals coalescing into carried slots, shown as ghosts with their progress. */
    coalescences?: readonly UICoalescence[];
    /** The selected carried slot, whose context the panel shows. */
    selectedSlotIndex: number | null;
    onSelectedSlotChange: (slotIndex: number | null) => void;
    /** The carried slot whose label is waiting for an action-bar key. */
    assigningSlotIndex: number | null;
    onAssigningSlotChange: (slotIndex: number | null) => void;
    /** A one-line message under the slots, e.g. why a move failed. */
    status?: string | null;
    /** What to show under the slots for the selected slot, if anything. */
    context?: ReactNode;
    /** Content for the panel's footer; no footer when unset. */
    footer?: ReactNode;
    /** The screen edge it slides off when closed (default left); its side label faces the other way. */
    edge?: HudEdge;
    /** How items compare with what the character wears (#293, #341), marked with chevrons. */
    itemComparisons?: UIItemComparisons;
    /**
     * What a slot holds, shown in a popover while the pointer rests on it
     * (#205), from its item and the coalescences headed for it (soonest
     * first). None when unset.
     */
    renderSlotDetails?: (item: UIInventoryItem | undefined, pending: readonly UICoalescence[]) => ReactNode;
}

const NO_COALESCENCES: readonly UICoalescence[] = [];

function groupBySlot(coalescences: readonly UICoalescence[]): Map<number, UICoalescence[]> {
    const bySlot = new Map<number, UICoalescence[]>();
    for (const coalescence of [...coalescences].sort((a, b) => a.completesAtMs - b.completesAtMs)) {
        bySlot.set(coalescence.targetSlotIndex, [...(bySlot.get(coalescence.targetSlotIndex) ?? []), coalescence]);
    }
    return bySlot;
}

/**
 * The carried inventory, on the left edge: in a run (see `RunCarriedPanel`)
 * and on the gathering page, where it previews the run's starting crystals.
 * Every carried slot shows the action-bar key linked to it on its label;
 * clicking a label waits for a key (handled by the loadout's action bar).
 * Clicking a slot selects it and expands the panel with the `context` the
 * caller gives for it; with nothing selected it shows a short how-to guide
 * instead. Dragging onto a slot moves the dragged item there through the
 * surrounding `ItemTransferProvider`; right-clicking an item opens its menu.
 */
export function CarriedPanel({
    open,
    onOpenChange,
    carriedItems,
    totalSlots,
    coalescences = NO_COALESCENCES,
    selectedSlotIndex,
    onSelectedSlotChange,
    assigningSlotIndex,
    onAssigningSlotChange,
    status = null,
    context,
    footer,
    edge = 'left',
    itemComparisons,
    renderSlotDetails,
}: CarriedPanelProps): JSX.Element {
    const { targets } = useActionBarTargets();
    const { selectedItemId, selectItem, clearSelectedItem, moveTo, openItemMenu } = useItemTransferContext();
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Closed, the panel is off screen but its side label stays usable.
        if (contentRef.current !== null) {
            contentRef.current.inert = !open;
        }
    }, [open]);

    const pendingBySlot = useMemo(() => groupBySlot(coalescences), [coalescences]);
    const contextSlotIndex = selectedSlotIndex !== null && selectedSlotIndex < totalSlots ? selectedSlotIndex : null;

    const actionKeyLabels = useActionBarKeyLabels();
    const hotkeyLabel = (slotIndex: number): string => actionKeyLabels[findCarriedSlotHotkeyIndex(targets, slotIndex)] ?? '';
    const sideLabel = useHudDrawerSideLabel(edge, 'Inventory', 'toggle-inventory');

    /** Selects a slot (or clears the selection), and its item for the loadout's frames. */
    const selectSlot = (slotIndex: number | null): void => {
        onAssigningSlotChange(null);
        onSelectedSlotChange(slotIndex);
        const item = slotIndex === null ? undefined : carriedItems[slotIndex];
        if (item === undefined) {
            clearSelectedItem();
        } else {
            selectItem(item.id, 'inventory');
        }
    };

    /** Moves the selected item (from here or the loadout) into a carried slot, and follows it there. */
    const moveSelectedTo = (slotIndex: number): void => {
        onAssigningSlotChange(null);
        if (selectedItemId === null || carriedItems[slotIndex]?.id === selectedItemId) {
            return;
        }
        moveTo('inventory', slotIndex);
        onSelectedSlotChange(slotIndex);
    };

    const handleLabelClick = (slotIndex: number): void => {
        // The page input ignores keys while a button has focus, and the
        // action-bar key that links this slot must reach it. In a run the game
        // takes focus back after any HUD click; elsewhere nothing does, so let
        // go of it here.
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        onAssigningSlotChange(assigningSlotIndex === slotIndex ? null : slotIndex);
    };

    const hasContext = contextSlotIndex !== null && context !== undefined && context !== null;

    return (
        <HudDrawer edge={edge} open={open} className={styles.carriedDrawer}>
            <HudPanel
                title='Carried'
                className={styles.carriedPanel}
                {...sideLabel}
                onSideLabelClick={() => onOpenChange(!open)}
                footerClassName={styles.footer}
                footer={footer}
            >
                <div ref={contentRef} className={styles.content} aria-hidden={!open}>
                    <div className={styles.slots} role='group' aria-label='Carried slots'>
                        {Array.from({ length: totalSlots }, (_, slotIndex) => {
                            const item = carriedItems[slotIndex];
                            const pending = pendingBySlot.get(slotIndex) ?? NO_COALESCENCES;
                            const label = hotkeyLabel(slotIndex);
                            const isAssigning = assigningSlotIndex === slotIndex;
                            return (
                                <HotkeySlot
                                    key={slotIndex}
                                    label={isAssigning ? '?' : label}
                                    ariaLabel={`Carried slot ${slotIndex + 1}${item === undefined ? ', empty' : `, ${item.name}`}`}
                                    isSelected={slotIndex === contextSlotIndex}
                                    onClick={() => selectSlot(selectedSlotIndex === slotIndex ? null : slotIndex)}
                                    onSecondaryClick={item === undefined || openItemMenu === undefined ? undefined : (anchor): void => openItemMenu(item.id, 'inventory', anchor)}
                                    onDragStart={item === undefined ? undefined : (): void => selectSlot(slotIndex)}
                                    onItemDrop={() => moveSelectedTo(slotIndex)}
                                    onLabelClick={() => handleLabelClick(slotIndex)}
                                    labelAriaLabel={label === ''
                                        ? `Link carried slot ${slotIndex + 1} to an action key`
                                        : `Carried slot ${slotIndex + 1} is on key ${label}; change it`}
                                    isLabelActive={isAssigning}
                                    rarity={item?.instance?.rarity}
                                    comparison={item === undefined ? undefined : itemComparisons?.[item.id]}
                                    popoverContent={renderSlotDetails?.(item, pending)}
                                >
                                    <CarriedSlotContent item={item} pending={pending} />
                                </HotkeySlot>
                            );
                        })}
                    </div>

                    {assigningSlotIndex !== null && (
                        <p className={styles.hint} role='status'>
                            {`Press an action slot's key or click the slot to link slot ${assigningSlotIndex + 1}. Press it again to unlink. Esc cancels.`}
                        </p>
                    )}

                    {status !== null && assigningSlotIndex === null && (
                        <p className={styles.hint} role='status'>{status}</p>
                    )}

                    {(hasContext || assigningSlotIndex === null) && (
                        <>
                            <div className={styles.divider} aria-hidden='true' />
                            {/* Only the context scrolls; the slots above it stay put. */}
                            <div className={styles.context}>
                                {hasContext ? context : <CarriedPanelGuide />}
                            </div>
                        </>
                    )}
                </div>
            </HudPanel>
        </HudDrawer>
    );
}
